import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import styles from './AuthCodeInput.module.scss';

interface AuthCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  ariaLabel?: string;
}

const LENGTH = 6;
const SLOTS = Array.from({ length: LENGTH }, (_, index) => index);

/**
 * The 6-digit code field (design: onboarding g6). One real input takes typing, paste and the
 * one-time-code autofill. Six boxes under it only draw the digits. The sixth digit submits.
 */
const AuthCodeInputComponent: React.FC<AuthCodeInputProps> = ({
  value,
  onChange,
  onComplete,
  disabled,
  error,
  ariaLabel,
}) => {
  const [focused, setFocused] = useState(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Strip non-digits (paste, autofill and typing alike) and cap at the code length.
      const digits = e.target.value.replace(/\D/g, '').slice(0, LENGTH);
      onChange(digits);
      if (digits.length === LENGTH) {
        onComplete?.(digits);
      }
    },
    [onChange, onComplete]
  );

  const boxes = useMemo(
    () =>
      SLOTS.map((index) => (
        <span
          key={index}
          className={clsx(styles.box, focused && index === Math.min(value.length, LENGTH - 1) && styles.on)}
        >
          {value.charAt(index)}
        </span>
      )),
    [focused, value]
  );

  return (
    <div className={clsx(styles.code, error === true && styles.bad, disabled === true && styles.off)}>
      <div className={styles.boxes} aria-hidden="true" data-testid="auth-code-boxes">
        {boxes}
      </div>
      <input
        className={styles.input}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={LENGTH}
        disabled={disabled}
        value={value}
        aria-label={ariaLabel ?? 'Verification code'}
        aria-invalid={error === true}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={handleChange}
      />
    </div>
  );
};

export const AuthCodeInput = TypedMemo(AuthCodeInputComponent);
